import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import type { ReactNode } from 'react'
import { useAuth } from '@/auth'
import { notificationService } from '@/services/notificationService'
import type { Notification, NotificationActionType, NotificationAction, NotificationScope } from '@/services/notificationService'

// Polling interval - 5 minutes (fallback when push is not enabled)
const POLLING_INTERVAL = 5 * 60 * 1000;  // 5 minutes

// Debounce delay for push-triggered refresh
const PUSH_DEBOUNCE_DELAY = 1000;  // 1 second

/**
 * Check if push notifications are enabled on this device.
 * If push is enabled, we rely on push messages instead of polling.
 */
async function isPushEnabled(): Promise<boolean> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return false;
  }
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return subscription !== null;
  } catch {
    return false;
  }
}

interface NotificationsContextType {
  notifications: Notification[];
  personalNotifications: Notification[];
  groupNotifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  lastUpdated: string | null;
  
  // Actions
  refetch: () => Promise<void>;
  markAsRead: (notificationId: string, scope?: NotificationScope, groupId?: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  dismiss: (notificationId: string) => Promise<void>;
  executeAction: (notificationId: string, actionType: NotificationActionType) => Promise<NotificationAction | null>;
  
  // Polling control
  pausePolling: () => void;
  resumePolling: () => void;
  isPaused: boolean;
}

const NotificationsContext = createContext<NotificationsContextType | null>(null)

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth()
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastUpdated, setLastUpdated] = useState<string | null>(null)
  const [isPaused, setIsPaused] = useState(false)
  
  // Refs for polling and debouncing
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const pushDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isTabVisibleRef = useRef(true)
  const pushEnabledRef = useRef(false)

  // Derived state: separate personal and group notifications
  const personalNotifications = notifications.filter(n => n.scope === 'personal')
  const groupNotifications = notifications.filter(n => n.scope === 'group')

  // Fetch full notifications list
  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) {
      setNotifications([])
      setUnreadCount(0)
      setLastUpdated(null)
      return
    }

    try {
      setIsLoading(true)
      setError(null)
      const response = await notificationService.list()
      setNotifications(response.data)
      setUnreadCount(response.meta.unreadCount)
      setLastUpdated(response.meta.lastUpdated)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch notifications')
    } finally {
      setIsLoading(false)
    }
  }, [isAuthenticated])

  // Lightweight polling - just get unread count
  const pollUnreadCount = useCallback(async () => {
    if (!isAuthenticated || isPaused) return
    
    try {
      const response = await notificationService.getUnreadCount()
      const newCount = response.data.unreadCount
      
      // If unread count increased, fetch full list to get new notifications
      if (newCount > unreadCount) {
        await fetchNotifications()
      } else {
        setUnreadCount(newCount)
        setLastUpdated(response.data.lastUpdated)
      }
    } catch (err) {
      // Silently fail on polling errors - don't spam the user
      console.warn('Failed to poll notification count:', err)
    }
  }, [isAuthenticated, isPaused, unreadCount, fetchNotifications])

  // Mark a single notification as read
  const markAsRead = useCallback(async (
    notificationId: string, 
    scope?: NotificationScope, 
    groupId?: string
  ) => {
    try {
      await notificationService.markAsRead(notificationId, { scope, groupId })
      
      // Update local state optimistically
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      )
      setUnreadCount(prev => Math.max(0, prev - 1))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to mark notification as read')
      // Refetch to get correct state
      await fetchNotifications()
    }
  }, [fetchNotifications])

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      await notificationService.markAllAsRead()
      
      // Update local state
      setNotifications(prev => prev.map(n => ({ ...n, read: true })))
      setUnreadCount(0)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to mark all notifications as read')
      await fetchNotifications()
    }
  }, [fetchNotifications])

  // Dismiss a notification
  const dismiss = useCallback(async (notificationId: string) => {
    try {
      await notificationService.dismiss(notificationId)
      
      // Remove from local state (or mark as read for group notifications)
      setNotifications(prev => {
        const notification = prev.find(n => n.id === notificationId)
        if (!notification) return prev
        
        // Group notifications can't be deleted, just dismissed (marked as read)
        if (notification.scope === 'group') {
          if (!notification.read) {
            setUnreadCount(count => Math.max(0, count - 1))
          }
          return prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
        }
        
        // Personal notifications are actually deleted
        if (!notification.read) {
          setUnreadCount(count => Math.max(0, count - 1))
        }
        return prev.filter(n => n.id !== notificationId)
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to dismiss notification')
      await fetchNotifications()
    }
  }, [fetchNotifications])

  // Execute an action on a notification
  const executeAction = useCallback(async (
    notificationId: string, 
    actionType: NotificationActionType
  ): Promise<NotificationAction | null> => {
    try {
      const response = await notificationService.executeAction(notificationId, actionType)
      
      // Mark notification as read locally
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      )
      
      // Update unread count if was unread
      const notification = notifications.find(n => n.id === notificationId)
      if (notification && !notification.read) {
        setUnreadCount(prev => Math.max(0, prev - 1))
      }
      
      return response.data.action
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to execute notification action')
      return null
    }
  }, [notifications])

  // Polling control
  const pausePolling = useCallback(() => {
    setIsPaused(true)
  }, [])

  const resumePolling = useCallback(() => {
    setIsPaused(false)
  }, [])

  // Setup polling (skip if push notifications are enabled)
  useEffect(() => {
    const setupPolling = async () => {
      // Clear existing interval
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }

      if (!isAuthenticated || isPaused) return

      // Check if push is enabled - if so, skip polling
      pushEnabledRef.current = await isPushEnabled()
      if (pushEnabledRef.current) {
        console.debug('Push notifications enabled, skipping polling')
        return
      }

      // Set up polling at 5 minute intervals
      pollingIntervalRef.current = setInterval(pollUnreadCount, POLLING_INTERVAL)
    }

    setupPolling()

    // Cleanup on unmount
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
      }
    }
  }, [isAuthenticated, isPaused, pollUnreadCount])

  // Handle tab visibility changes - poll when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = async () => {
      isTabVisibleRef.current = document.visibilityState === 'visible'
      
      // If tab becomes visible, immediately poll to catch up
      if (isTabVisibleRef.current && isAuthenticated && !isPaused) {
        // Re-check push status (user might have enabled/disabled)
        pushEnabledRef.current = await isPushEnabled()
        
        // Always poll once when tab becomes visible (catch up)
        pollUnreadCount()
        
        // Only set up interval if push is not enabled
        if (!pushEnabledRef.current) {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current)
          }
          pollingIntervalRef.current = setInterval(pollUnreadCount, POLLING_INTERVAL)
        }
      } else if (!isTabVisibleRef.current) {
        // Stop polling when tab is hidden (save resources)
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current)
          pollingIntervalRef.current = null
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [isAuthenticated, isPaused, pollUnreadCount])

  // Listen for push notification messages from service worker
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return

    const handleServiceWorkerMessage = (event: MessageEvent) => {
      if (event.data?.type === 'PUSH_RECEIVED') {
        console.debug('Push notification received, debouncing refresh')
        
        // Clear any pending debounce
        if (pushDebounceRef.current) {
          clearTimeout(pushDebounceRef.current)
        }
        
        // Debounce the refresh to handle multiple rapid push notifications
        pushDebounceRef.current = setTimeout(() => {
          pollUnreadCount()
          pushDebounceRef.current = null
        }, PUSH_DEBOUNCE_DELAY)
      }
    }

    navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage)
    
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage)
      if (pushDebounceRef.current) {
        clearTimeout(pushDebounceRef.current)
      }
    }
  }, [pollUnreadCount])

  // Initial fetch
  useEffect(() => {
    fetchNotifications()
  }, [fetchNotifications])

  return (
    <NotificationsContext.Provider value={{
      notifications,
      personalNotifications,
      groupNotifications,
      unreadCount,
      isLoading,
      error,
      lastUpdated,
      refetch: fetchNotifications,
      markAsRead,
      markAllAsRead,
      dismiss,
      executeAction,
      pausePolling,
      resumePolling,
      isPaused,
    }}>
      {children}
    </NotificationsContext.Provider>
  )
}

export function useNotifications() {
  const context = useContext(NotificationsContext)
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationsProvider')
  }
  return context
}
